import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import styled, { useTheme } from 'styled-components';

import { Flex } from '@ui-library/flex';
import { Modal } from '@ui-library/modalV2';

import {
  CrossCloseButton,
  ModalBreadcrumb,
  MultiStepModalFooter,
  ModalHeader,
} from './modalContent';
import { MultiStepModalCtx, MultiStepModalStore, useMultiStepModalStore } from './modalStore';
import {
  ModalFooterProps,
  ModalHeaderProps,
  ModalStepProps,
  MultiStepModalProps,
  ModalSize,
} from './types';

const stepModalWidths: Record<ModalSize, string> = {
  sm: '400px',
  md: '600px',
  lg: '800px',
  xl: '968px',
  xxl: '1280px',
};

const StepContainer = styled(Flex)<{ $size: ModalSize }>`
  position: relative;
  width: ${({ $size = 'md' }) => stepModalWidths[$size]};
  max-width: 100%;
`;

const MultiStepModalContent: React.FC<MultiStepModalProps> = observer(
  ({ steps, open, maskClosable, destroyOnClose, size = 'md', ...props }) => {
    const store = useMemo(() => new MultiStepModalStore(steps, { size, ...props }), []);
    const ModalContent = store.currentStep?.component;

    return (
      <MultiStepModalCtx.Provider value={store}>
        {ModalContent && <ModalContent />}
      </MultiStepModalCtx.Provider>
    );
  },
);

export const MultiStepModal: React.FC<MultiStepModalProps> = observer((props) => {
  const { open, maskClosable = false, destroyOnClose = true, onClose, variant } = props;

  return (
    <Modal
      variant={variant}
      width="fit-content"
      open={open}
      maskClosable={maskClosable}
      destroyOnClose={destroyOnClose}
      closable={false}
      onCancel={onClose}
      footer={null}
      centered
    >
      <MultiStepModalContent {...props} />
    </Modal>
  );
});

export const ModalStep: React.FC<ModalStepProps> = ({
  children,
  loading = false,
  disabled = false,
  onNext,
  nextStepKey,
  header,
  footer,
  ...props
}) => {
  const store = useMultiStepModalStore();
  const theme = useTheme();

  useEffect(() => {
    store.setNextCallback(onNext);
  }, [onNext]);

  const { showCross, breadcrumb, size } = useMemo(
    () => ({
      showCross: props.showCross ?? store.config.showCross,
      size: props.size ?? store.config.size,
      breadcrumb: {
        ...store.config.breadcrumb,
        ...props.breadcrumb,
        allowNavigation:
          store.config.breadcrumb?.allowNavigation ?? props.breadcrumb?.allowNavigation ?? true,
      },
    }),
    [props.showCross, props.size, props.breadcrumb],
  );

  return (
    <StepContainer flexDirection="column" gap={`${theme.token.marginLG}px`} $size={size || 'md'}>
      {showCross && <CrossCloseButton disabled={loading} />}

      {(!breadcrumb?.hide || !!header) && (
        <Flex flexDirection="column" gap="8px">
          {!breadcrumb?.hide && (
            <ModalBreadcrumb
              nextPathKey={nextStepKey}
              allowNavigation={!!breadcrumb?.allowNavigation && !loading}
              showFullPath={breadcrumb?.showFullPath}
            />
          )}
          {!React.isValidElement(header)
            ? header && <ModalHeader {...(header as ModalHeaderProps)} />
            : header}
        </Flex>
      )}

      <Flex flexDirection="column" {...props}>
        {children}
      </Flex>

      {!React.isValidElement(footer) ? (
        <MultiStepModalFooter
          loading={loading}
          disabled={disabled}
          nextStepKey={nextStepKey}
          {...(footer as ModalFooterProps)}
        />
      ) : (
        footer
      )}
    </StepContainer>
  );
};
